import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import './GameEntry.css';

function GameEntry({ gameName, onBack, gameType = 'single-digit' }) {
  const location = useLocation();
  const toast = useToast();
  const [selectedGameType, setSelectedGameType] = useState(() => {
    if (location.state?.gameType) {
      // Map from selection format to internal format
      const typeMap = {
        'single-digit': 'single_digit',
        'jodi': 'jodi_digit',
        'single-panna': 'single_panna',
        'double-panna': 'double_panna',
        'triple-panna': 'double_panna',
        'half-sangam': 'half_sangam_open_digit',
        'full-sangam': 'full_sangam'
      };
      return typeMap[location.state.gameType] || 'single_digit';
    }
    return 'single_digit';
  });
  const [availableDigits, setAvailableDigits] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [filteredDigits, setFilteredDigits] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDigit, setSelectedDigit] = useState('');
  const [points, setPoints] = useState('');
  const [bids, setBids] = useState([]);
  const [totalBids, setTotalBids] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [showEntryAdded, setShowEntryAdded] = useState(false);
  const [marketStatus, setMarketStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const dropdownRef = useRef(null);

  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime || '18:00';

  // Get available game types based on market status
  const getAvailableGameTypes = () => {
    const baseTypes = {
      single_digit: 'Single Digit',
      jodi_digit: 'Jodi Digit',
      single_panna: 'Single Panna',
      double_panna: 'Double Panna',
      full_sangam: 'Full Sangam',
    };

    // For half sangam, show different options based on market status
    if (marketStatus === 'Open') {
      return {
        ...baseTypes,
        half_sangam_open_digit: 'Half Sangam - Open Digit',
        half_sangam_close_pana: 'Half Sangam - Close Pana',
      };
    } else if (marketStatus === 'Close') {
      return {
        ...baseTypes,
        half_sangam_close_digit: 'Half Sangam - Close Digit',
        half_sangam_open_pana: 'Half Sangam - Open Pana',
      };
    }

    return baseTypes;
  };

  // Game type labels
  const gameTypeLabels = {
    single_digit: 'Single Digit',
    jodi_digit: 'Jodi Digit',
    single_panna: 'Single Panna',
    double_panna: 'Double Panna',
    half_sangam_open_digit: 'Half Sangam - Open Digit',
    half_sangam_close_pana: 'Half Sangam - Close Pana',
    half_sangam_close_digit: 'Half Sangam - Close Digit',
    half_sangam_open_pana: 'Half Sangam - Open Pana',
    full_sangam: 'Full Sangam',
  };

  // Map game type to required digit count
  const getDigitCount = (gameType) => {
    const digitCountMap = {
      single_digit: 1,
      jodi_digit: 2,
      single_panna: 3,
      double_panna: 3,
      half_sangam_open_digit: 4,
      half_sangam_close_pana: 4,
      half_sangam_close_digit: 4,
      half_sangam_open_pana: 4,
      full_sangam: 8,
    };
    return digitCountMap[gameType];
  };

  // Get placeholder text based on game type
  const getPlaceholderText = (gameType) => {
    const placeholderMap = {
      single_digit: 'Enter Single Digit',
      jodi_digit: 'Enter Jodi Digit',
      single_panna: 'Enter Single Panna',
      double_panna: 'Enter Double Panna',
      half_sangam_open_digit: 'Enter Half Sangam',
      half_sangam_close_pana: 'Enter Half Sangam',
      half_sangam_close_digit: 'Enter Half Sangam',
      half_sangam_open_pana: 'Enter Half Sangam',
      full_sangam: 'Enter Full Sangam',
    };
    return placeholderMap[gameType] || 'Enter number';
  };

  // Fetch game data from API
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://backend.mainbazar777.site/api/game-ank');
        const data = await response.json();

        if (data.game_ank && data.game_ank.length > 0) {
          const gameData = data.game_ank[0];
          setAvailableDigits(gameData);
        }
        setError('');
      } catch (err) {
        setError('Failed to load game data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, []);

  // Check market status
  useEffect(() => {
    const checkOpenTime = () => {
      const now = new Date();
      const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const currentHours = kolkataTime.getHours();
      const currentMinutes = kolkataTime.getMinutes();
      const currentTimeInMinutes = currentHours * 60 + currentMinutes;

      const [openHours, openMinutes] = openTime.split(':').map(num => parseInt(num));
      const openTimeInMinutes = openHours * 60 + openMinutes;

      if (currentTimeInMinutes > openTimeInMinutes) {
        setMarketStatus('Close');
      } else {
        setMarketStatus('Open');
      }
    };

    checkOpenTime();
  }, [openTime]);

  // Update selectedGameType when market status changes
  useEffect(() => {
    const availableTypes = getAvailableGameTypes();

    // If current game type is not available in new market status, reset to single_digit
    if (!availableTypes[selectedGameType]) {
      setSelectedGameType('single_digit');
    }
  }, [marketStatus]);

  // Filter digits based on search input
  useEffect(() => {
    if (!searchInput.trim()) {
      setFilteredDigits([]);
      return;
    }

    if (Object.keys(availableDigits).length === 0) {
      setFilteredDigits([]);
      return;
    }

    const digitList = availableDigits[selectedGameType];
    if (!digitList) {
      setFilteredDigits([]);
      return;
    }

    let parsed = digitList;
    if (typeof digitList === 'string') {
      try {
        parsed = JSON.parse(digitList);
      } catch (e) {
        console.error('Error parsing digit list:', e);
        setFilteredDigits([]);
        return;
      }
    }

    // Filter digits based on required digit count for the game type
    const requiredDigitCount = getDigitCount(selectedGameType);
    let filtered = parsed.filter(digit => {
      const digitStr = digit.toString();
      return digitStr.length === requiredDigitCount && /^\d+$/.test(digitStr);
    });

    filtered = filtered
      .filter(digit => digit.toString().includes(searchInput.trim()))
      .slice(0, 10); // Show max 10 results

    setFilteredDigits(filtered);
  }, [searchInput, selectedGameType, availableDigits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDigitSelect = (digit) => {
    setSelectedDigit(digit);
    setSearchInput('');
    setShowDropdown(false);
  };

  const isRestrictedGame = (gameTypeKey) => {
    return ['jodi_digit', 'double_panna', 'full_sangam'].includes(gameTypeKey);
  };

  const isOpenTimePassed = () => {
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentHours = kolkataTime.getHours();
    const currentMinutes = kolkataTime.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    const [openHours, openMinutes] = openTime.split(':').map(num => parseInt(num));
    const openTimeInMinutes = openHours * 60 + openMinutes;

    return currentTimeInMinutes > openTimeInMinutes;
  };

  const handleAddEntry = () => {
    if (!selectedDigit || !points.trim()) {
      toast.error('Please select a digit and enter points');
      return;
    }

    // Check if restricted game and open time has passed
    if (isRestrictedGame(selectedGameType) && isOpenTimePassed()) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    // Validate digit count based on game type
    const requiredDigitCount = getDigitCount(selectedGameType);
    const digitStr = selectedDigit.toString();
    if (digitStr.length !== requiredDigitCount) {
      toast.error(`${gameTypeLabels[selectedGameType]} requires exactly ${requiredDigitCount}-digit numbers`);
      return;
    }

    const newEntry = {
      id: Date.now(),
      pana: selectedDigit,
      point: points,
      type: gameTypeLabels[selectedGameType] || selectedGameType
    };

    setBids([...bids, newEntry]);
    setTotalBids(totalBids + 1);
    setTotalPoints(totalPoints + parseInt(points));

    setShowEntryAdded(true);
    setTimeout(() => setShowEntryAdded(false), 2000);

    setSelectedDigit('');
    setPoints('');
    setSearchInput('');
  };

  const handleDeleteEntry = (id) => {
    const entry = bids.find(b => b.id === id);
    setBids(bids.filter(b => b.id !== id));
    setTotalBids(totalBids - 1);
    setTotalPoints(totalPoints - parseInt(entry.point));
  };

  const isOpenDisabled = () => {
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentHours = kolkataTime.getHours();
    const currentMinutes = kolkataTime.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    const [openHours, openMinutes] = openTime.split(':').map(num => parseInt(num));
    const openTimeInMinutes = openHours * 60 + openMinutes;

    return currentTimeInMinutes > openTimeInMinutes;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const handleSubmit = () => {
    if (!selectedDigit || !points.trim()) {
      toast.error('Please select a digit and enter funds');
      return;
    }
    toast.success(`Bid Placed: ${gameTypeLabels[selectedGameType]} ${selectedDigit}, Funds ${points}`);
    setSelectedDigit('');
    setPoints('');
    setSearchInput('');
    setBids([]);
    setTotalBids(0);
    setTotalPoints(0);
  };

  const today = new Date();
  const dateString = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

  if (loading) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">Loading...</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="loading-message">Loading game data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">Error</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-entry">
      {/* Header */}
      <header className="game-entry-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="game-title">{gameTypeLabels[selectedGameType] || gameName || 'GAME ENTRY'}</h1>
        <div style={{ width: '24px' }}></div>
      </header>

      {/* Main Content */}
      <div className="game-content">
        {/* Game Type Selection - Only show if not passed from previous route */}
        {!location.state?.gameType && (
          <div className="game-type-section">
            <label className="section-label">Select Game Type:</label>
            <select
              value={selectedGameType}
              onChange={(e) => {
                setSelectedGameType(e.target.value);
                setSelectedDigit('');
                setSearchInput('');
                setFilteredDigits([]);
              }}
              className="game-type-select"
            >
              {Object.entries(getAvailableGameTypes()).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Game Info Section */}
        <div className="game-info-section">
          <div className="info-row">
            <div className="info-item">
              <label>Date</label>
              <input type="text" value={dateString} readOnly disabled />
            </div>
            <div className="info-item">
              <label>Status</label>
              <select
                value={marketStatus}
                onChange={(e) => setMarketStatus(e.target.value)}
                className={`status-select status-${marketStatus.toLowerCase()}`}
              >
                <option value="Open" disabled={isOpenDisabled()}>Open</option>
                <option value="Close">Close</option>
              </select>
            </div>
          </div>

          <div className="time-section">
            <div className="time-item">
              <span className="time-label">Time Open:</span>
              <span className="time-value">{formatTime(openTime)}</span>
            </div>
            <div className="time-item">
              <span className="time-label">Time Close:</span>
              <span className="time-value">{formatTime(closeTime)}</span>
            </div>
          </div>
        </div>

        {/* Input Section with Searchable Dropdown */}
        <div className="input-section">
          <div className="input-row">
            <div className="input-item" ref={dropdownRef}>
              <label>Select Digit/Number:</label>
              <div className="dropdown-container">
                <input
                  type="text"
                  placeholder={`${getPlaceholderText(selectedGameType)}...`}
                  value={searchInput || selectedDigit}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setShowDropdown(true);
                    if (!e.target.value) {
                      setSelectedDigit('');
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="digit-input"
                  autoComplete="off"
                />
                {showDropdown && filteredDigits.length > 0 && (
                  <div className="dropdown-menu">
                    {filteredDigits.map((digit, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() => handleDigitSelect(digit)}
                      >
                        {digit}
                      </div>
                    ))}
                  </div>
                )}
                {showDropdown && searchInput && filteredDigits.length === 0 && (
                  <div className="dropdown-menu">
                    <div className="dropdown-item disabled">No results found</div>
                  </div>
                )}
              </div>
            </div>
            <div className="input-item">
              <label>Enter Funds:</label>
              <input
                type="number"
                placeholder="Funds"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="points-input"
              />
            </div>
          </div>

          <button className="add-btn" onClick={handleAddEntry}>
            Add Entry
          </button>
        </div>

        {/* Entries Summary */}
        {bids.length > 0 && (
          <>
            <div className="summary-section">
              <div className="summary-item">
                <span className="summary-label">Total Entries</span>
                <span className="summary-value">{totalBids}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Funds</span>
                <span className="summary-value">{totalPoints}</span>
              </div>
            </div>

            {/* Entries Table */}
            <div className="bids-table-section">
              <div className="table-header">
                <div className="header-cell">Number</div>
                <div className="header-cell">Type</div>
                <div className="header-cell">Funds</div>
                <div className="header-cell">Action</div>
              </div>
              <div className="table-body">
                {bids.map((bid) => (
                  <div key={bid.id} className="table-row">
                    <div className="table-cell">{bid.pana}</div>
                    <div className="table-cell">{bid.type}</div>
                    <div className="table-cell">₹{bid.point}</div>
                    <div className="table-cell">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteEntry(bid.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Submit Button */}
        {bids.length > 0 && (
          <button className="submit-btn" onClick={handleSubmit}>
            Submit Entries
          </button>
        )}
      </div>

      {/* Entry Added Notification */}
      {showEntryAdded && (
        <div className="bid-placed-notification">
          ✓ Entry Added Successfully
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="toast-notification">
          Time Up!
        </div>
      )}
    </div>
  );
}

export default GameEntry;
